import React from 'react';
import Dialog from '@components/atoms/dialog/dialog';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next';
import sanitizer from '@helpers/sanitizer';
import { formatDate, formatTime } from '@helpers/utils/date-time-formatter-helper';
import useUser from '@hooks/user/use-user';
import styles from './chat-ban-modal.module.scss';

export enum EnumBanType {
  TEMP_BAN = 'TEMP_BAN',
  PERMA_BAN = 'PERMA_BAN',
  LINK_BLOCK = 'LINK_BLOCK',
}

type ChatBanModalProps = {
  isOpen: boolean;
  banType: EnumBanType;
  onClose: React.Dispatch<React.SetStateAction<boolean>>;
  banEndTime?: Date;
};

const ChatBanModal: React.FC<ChatBanModalProps> = (props) => {
  const { isOpen, banType, onClose, banEndTime } = props;
  const { t } = useTranslation();

  const { user } = useUser();

  const handleCloseDialog = () => {
    onClose(false);
  };

  return (
    <Dialog
      isOpen={isOpen}
      closeDialog={() => handleCloseDialog()}
      key={`openBanChatModal${isOpen}`}
      hasCloseIcon
      styles={{
        content: {
          maxWidth: '600px',
        },
      }}
    >
      {(modalDialogTitleId: string) => (
        <div className={clsx(styles.chatBanModal, 'vc-modal__innerContainer')}>
          {banType === EnumBanType.TEMP_BAN && (
            <>
              <h2
                className={clsx(styles.chatBanModal__title, 'vc-modal__title')}
                id={modalDialogTitleId}
              >
                {t('CHAT.BANMODAL.DAY_BAN.TITLE')}
              </h2>
              <div className={clsx(styles.chatBanModal__text)}>
                {t('CHAT.BANMODAL.DAY_BAN.TEXT', {
                  endTime: formatTime(banEndTime, user.countryISO),
                  endDate: formatDate(banEndTime, false, user.countryISO),
                })}
              </div>
              <ul
                className={clsx(styles.chatBanModal__list)}
                dangerouslySetInnerHTML={{ __html: sanitizer(t(`CHAT.BANMODAL.DAY_BAN.LIST`)) }}
              ></ul>
              <div
                className={clsx(styles.chatBanModal__warningbox)}
                dangerouslySetInnerHTML={{
                  __html: sanitizer(t('CHAT.BANMODAL.DAY_BAN.BOTTOM_TEXT')),
                }}
              />
            </>
          )}
          {banType === EnumBanType.PERMA_BAN && (
            <>
              <h2
                className={clsx(styles.chatBanModal__title, 'vc-modal__title')}
                id={modalDialogTitleId}
              >
                {t('CHAT.BANMODAL.PERMA_BAN.TITLE')}
              </h2>
              <div className={clsx(styles.chatBanModal__text)}>{t('CHAT.BANMODAL.PERMA_BAN.TEXT')}</div>
              <ul
                className={clsx(styles.chatBanModal__list)}
                dangerouslySetInnerHTML={{ __html: sanitizer(t(`CHAT.BANMODAL.PERMA_BAN.LIST`)) }}
              ></ul>
            </>
          )}
          {banType === EnumBanType.LINK_BLOCK && (
            <>
              <h2
                className={clsx(styles.chatBanModal__title, 'vc-modal__title')}
                id={modalDialogTitleId}
              >
                {t('CHAT.BANMODAL.LINK_BLOCK.TITLE')}
              </h2>
              <div className={clsx(styles.chatBanModal__text)}>{t('CHAT.BANMODAL.LINK_BLOCK.TEXT')}</div>
              <ul
                className={clsx(styles.chatBanModal__list)}
                dangerouslySetInnerHTML={{ __html: sanitizer(t(`CHAT.BANMODAL.LINK_BLOCK.LIST`)) }}
              ></ul>
              <div
                className={clsx(styles.chatBanModal__warningbox)}
                dangerouslySetInnerHTML={{
                  __html: sanitizer(t('CHAT.BANMODAL.LINK_BLOCK.WARNINGMESSAGE')),
                }}
              />
            </>
          )}
          <button
            type="button"
            data-cy="chat-ban-modal-accept-button"
            onClick={() => handleCloseDialog()}
            className={clsx('vc-btn', 'vc-btn--primary', 'vc-btn--medium', styles.chatBanModal__btn)}
          >
            {t('CHAT.MODAL.GOTIT')}
          </button>
        </div>
      )}
    </Dialog>
  );
};

export default ChatBanModal;
